import { CSSProperties, useMemo, Ref } from 'react';

import { useBoundingClientRect } from './use-bounding-client-rectangle';

export const useContainSize = <T extends HTMLElement>({
    width,
    height,
    recalcOnResize = false,
}: {
    width: number;
    height: number;
    /** Default: false */
    recalcOnResize?: boolean;
}): [CSSProperties, Ref<T>, () => void] => {
    const [containerRect, containerRef, refresh] = useBoundingClientRect<T>({
        recalcOnResize,
    });

    const childrenStyle = useMemo<CSSProperties>(() => {
        if (!containerRect) {
            return {};
        }
        if (width === 0 || height === 0) {
            return {
                width: '100%',
                height: '100%',
            };
        }
        const containerRatio = containerRect.width / containerRect.height;

        const targetRatio = width / height;
        if (containerRatio > targetRatio) {
            const scale = containerRect.height / height;
            return {
                height: containerRect.height,
                width: width * scale,
                left: (containerRect.width - width * scale) / 2,
            };
        }
        const scale = containerRect.width / width;
        return {
            height: height * scale,
            width: containerRect.width,
            top: (containerRect.height - height * scale) / 2,
        };
    }, [width, height, containerRect]);

    return [childrenStyle, containerRef, refresh];
};
